import { DriverStepConfig } from "../steps";
import { waitForElement, waitForUserAction } from "../utils";

function showPulseHint(container: HTMLElement, x: number, y: number): void {
    if (!document.getElementById('tour-pulse-style')) {
        const style = document.createElement('style');
        style.id = 'tour-pulse-style';
        style.textContent = `
            @keyframes tour-pulse {
                0%   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
                80%  { opacity: 0.5; }
                100% { transform: translate(-50%, -50%) scale(2.8); opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }
    const hint = document.createElement('div');
    hint.id = 'tour-pulse-hint';
    hint.style.cssText = `
        position: absolute;
        left: ${x}px;
        top: ${y}px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 3px solid #c800c8;
        pointer-events: none;
        z-index: 9999;
        animation: tour-pulse 1.4s ease-out infinite;
    `;
    container.style.position = 'relative';
    container.appendChild(hint);
}

/**
 * Экскурсия по csv графикам в галлере
 */
export const galleryCsvSteps: DriverStepConfig[] = [

    {
        element: '.folder-wrapper',
        popover: {
            title: 'Папка с графикми',
            description: 'Чтобы увидеть сохраненные графики - вам требуется нажать на кнопку папки',
            side: "bottom",
            align: 'start',
            showButtons: ['close'],
        },
        waitFor: () => waitForUserAction(resolve => {

            const wrappers = document.querySelectorAll('.folder-wrapper');
            if (!wrappers.length) return;

            // Папка уже раскрыта — переходим сразу
            const alreadyExpanded = Array.from(wrappers).some(w => w.classList.contains('expanded'));
            if (alreadyExpanded) { resolve(); return; }

            const btn = wrappers[0];
            if (!btn) return;
            btn.addEventListener('click', async () => {
                await waitForElement('.folder-wrapper .sector-flux-checkbox');
                resolve();
            }, { once: true });
        })
    },

    {
        element: '.folder-wrapper .sector-flux-checkbox',
        popover: {
            title: 'Интерактивный график потоков',
            description: 'Давайте откроем график потоков',
            side: "bottom",
            align: 'start',
            showButtons: ['close'],
        },
        waitFor: () => waitForUserAction(resolve => {

            // График уже отрисован — переходим сразу
            if (document.querySelector('.gallery-main .graph-interactive')) { resolve(); return; }

            const checkbox = document.querySelector('.folder-wrapper .sector-flux-checkbox') as HTMLInputElement;
            if (!checkbox) return;

            checkbox.addEventListener('click', async () => {
                // checked обновляется ДО события: если стал true — график открывается
                if (checkbox.checked) {
                    await waitForElement('.gallery-main .graph-interactive');
                    resolve();
                }
                // если пользователь снял галочку — ждём повторного клика (слушатель once сработал,
                // поэтому вешаем новый через рекурсию не нужно — просто сообщаем пользователю)
            }, { once: true });
        })
    },

    // {
    //     element: '.gallery-main .graph-interactive',
    //     popover: {
    //         title: 'Интерактивный график потоков',
    //         description: 'Давайте откроем график потоков',
    //         side: "bottom",
    //         align: 'start',
    //         showButtons: ['close'],
    //     },
    //     waitFor: () => waitForUserAction(resolve => {})
    // },

    {
        element: '.graph-fullscreen-btn',
        popover: {
            title: 'Интерактивный график потоков',
            description: 'Давайте откроем график потоков',
            side: "bottom",
            align: 'start',
            showButtons: ['close'],
        },
        waitFor: () => waitForUserAction(resolve => {

            // Фуллскрин уже открыт — переходим сразу
            if (document.querySelector('.fullscreen-content.fullscreen-chart')) { resolve(); return; }

            const btn = document.querySelector('.graph-fullscreen-btn') as HTMLInputElement;
            if (!btn) return;

            btn.addEventListener('click', async () => {
                await waitForElement('.fullscreen-content.fullscreen-chart');
                resolve();
            }, { once: true });
        })
    },

    {
        element: '.fullscreen-content.fullscreen-chart',
        popover: {
            title: 'Шаг 1: выберите первую точку',
            description: 'Нажмите на любую <b>фиолетовую точку</b> на графике — она обозначает зону высокого потока. После клика появится жёлтый маркер.',
            side: "top",
            align: 'center',
            showButtons: ['close'],
        },
        waitFor: () => waitForUserAction(resolve => {
            const container = document.querySelector('.fullscreen-content.fullscreen-chart') as HTMLElement;
            if (!container) return;

            const tryShowHint = () => {
                const x = container.dataset.tourHintX;
                const y = container.dataset.tourHintY;
                if (x && y) showPulseHint(container, Number(x), Number(y));
            };

            if (container.dataset.tourHintX) {
                tryShowHint();
            } else {
                const observer = new MutationObserver((_, obs) => {
                    if (container.dataset.tourHintX) {
                        obs.disconnect();
                        tryShowHint();
                    }
                });
                observer.observe(container, { attributes: true, attributeFilter: ['data-tour-hint-x'] });
                setTimeout(() => observer.disconnect(), 5000);
            }

            container.addEventListener('click', () => {
                document.getElementById('tour-pulse-hint')?.remove();
                resolve();
            }, { once: true });
        })
    },

    {
        element: '.fullscreen-content.fullscreen-chart',
        popover: {
            title: 'Шаг 2: выберите вторую точку',
            description: 'Теперь нажмите на <b>любую другую точку</b>, до которой хотите измерить расстояние. Между точками появится линия, и начнётся расчёт толщины блока.',
            side: "top",
            align: 'center',
            showButtons: ['close'],
        },
        waitFor: () => waitForElement('#thickness-result-badge', 300000)
    },

    {
        element: '#thickness-result-badge',
        popover: {
            title: 'Результат расчёта',
            description: 'Здесь отображается <b>толщина блока в метрах</b> между двумя выбранными точками. Чтобы начать заново — просто нажмите на новую точку. (Если расчитанное расстояние равно 0 м - попробуйте указать точку ниже фиолетовой)',
            side: "bottom",
            align: 'center',
            showButtons: ['next'],
            doneBtnText: 'Завершить',
        },
    },
]

